import { computed, ref } from "vue";
import { UserAPI } from "@/api";
import type { User } from "@/types";

/**
 * Whether the session is currently authorized.
 */
const isAuthorized = ref(false);

/**
 * The current user object.
 */
const user = ref<User>({} as User);

/**
 * Whether the user is a super‑admin.
 */
const isSuperAdmin = computed(() => user.value.role === "super_admin");

/**
 * Whether the user is an admin.
 */
const isAdmin = computed(() => user.value.role === "admin");

/**
 * Mark the session as authorized.
 */
function authorize(): void {
  isAuthorized.value = true;
}

/**
 * Mark the session as unauthorized.
 */
function unAuthorize(): void {
  isAuthorized.value = false;
}

/**
 * Initialize the current user by fetching from API.
 */
async function initUser(): Promise<void> {
  user.value = await UserAPI.fetchCurrentUser();

  if (user.value) {
    const fetchedProfilePicture = await UserAPI.fetchProfilePictureById(user.value.id);
    user.value.profile_picture = fetchedProfilePicture.profile_picture;
  }
}

/**
 * Composable for session state and actions.
 */
export function useSession() {
  return {
    isAuthorized,
    isSuperAdmin,
    isAdmin,
    authorize,
    unAuthorize,
    user,
    initUser,
  };
}
